/**
 * Push Notifications Service for Capacitor (iOS/Android)
 * 
 * Usage:
 * 1. Import: import { initializePushNotifications } from './services/pushNotifications';
 * 2. Initialize in App.js: useEffect(() => { initializePushNotifications(); }, []);
 */

import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Store pending token if received before auth
let pendingToken = null;

// Register FCM token listener IMMEDIATELY on module load
if (typeof window !== 'undefined') {
  window.addEventListener('fcmToken', async (event) => {
    const token = event.detail?.token;
    if (token) {
      console.log('✅ FCM Token received from native (early):', token.substring(0, 20) + '...');
      
      // Check if user is authenticated
      const authToken = sessionStorage.getItem('access_token');
      if (authToken) {
        // User is logged in, register immediately
        await registerDeviceToken(token);
      } else {
        // User not logged in yet, store for later
        console.log('ℹ️ User not authenticated yet, storing token for later registration');
        pendingToken = token;
        localStorage.setItem('pending_fcm_token', token);
      }
    }
  });
  console.log('🔔 FCM Token listener registered (early)');
}

/**
 * Initialize push notifications
 * - Request permission
 * - Register device token
 * - Setup notification handlers
 */
export const initializePushNotifications = async () => {
  try {
    // Check if running on native platform
    if (!Capacitor.isNativePlatform()) {
      console.log('ℹ️ Push notifications only available on native platforms');
      return false;
    }
    
    console.log('🔔 Initializing push notifications...');
    
    // Check if platform supports push notifications
    const permStatus = await PushNotifications.checkPermissions();
    
    if (permStatus.receive === 'prompt') {
      // Request permission
      const permission = await PushNotifications.requestPermissions();
      
      if (permission.receive !== 'granted') {
        console.log('❌ Push notification permission denied');
        return false;
      }
    }
    
    if (permStatus.receive === 'denied') {
      console.log('❌ Push notification permission denied');
      return false;
    }
    
    // Register for push notifications
    await PushNotifications.register();
    
    // Listen for registration success
    PushNotifications.addListener('registration', async (token) => {
      console.log('✅ Push notification token from Capacitor:', token.value);
      
      // Register token with backend
      await registerDeviceToken(token.value);
    });
    
    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      console.error('❌ Push notification registration error:', error);
    });
    
    // Listen for push notifications received (app in foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('📩 Push notification received:', notification);
      
      // Show notification banner (you can customize this)
      if (notification.title || notification.body) {
        // Use a toast or custom UI instead of alert for better UX
        console.log(`📩 ${notification.title}: ${notification.body}`);
      }
    });
    
    // Listen for push notification clicked/opened
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('🔔 Push notification action performed:', notification);
      
      // Handle notification click
      handleNotificationClick(notification.notification);
    });
    
    console.log('✅ Push notifications initialized successfully');
    
    // Check for pending token that was received before login
    const storedPendingToken = localStorage.getItem('pending_fcm_token');
    if (storedPendingToken) {
      console.log('📤 Found pending FCM token, registering now...');
      await registerDeviceToken(storedPendingToken);
      localStorage.removeItem('pending_fcm_token');
    }
    
    // Also check the module-level pending token
    if (pendingToken) {
      console.log('📤 Found module pending FCM token, registering now...');
      await registerDeviceToken(pendingToken);
      pendingToken = null;
    }
    
    // Listen for FCM token from native code (in case it arrives later)
    window.addEventListener('fcmToken', async (event) => {
      const token = event.detail?.token;
      if (token) {
        console.log('✅ FCM Token received from native:', token.substring(0, 20) + '...');
        await registerDeviceToken(token);
      }
    });
    
    // Try to get FCM token directly after a short delay
    // (Firebase might generate it after the initial registration)
    setTimeout(async () => {
      try {
        // Try to get the token that might have been stored by native code
        const storedToken = localStorage.getItem('fcm_token');
        if (!storedToken) {
          console.log('ℹ️ No stored FCM token yet, waiting for native callback...');
        }
      } catch (e) {
        console.log('ℹ️ Could not check for stored token:', e);
      }
    }, 2000);
    
    return true;
    
  } catch (error) {
    console.error('❌ Error initializing push notifications:', error);
    return false;
  }
};

/**
 * Register device token with backend
 */
export const registerDeviceToken = async (token) => {
  try {
    if (!token) {
      console.log('⚠️ No token provided, skipping registration');
      return;
    }
    
    const authToken = sessionStorage.getItem('access_token');
    
    if (!authToken) {
      console.log('⚠️ No auth token, skipping device registration');
      return;
    }
    
    // Get platform
    const platform = getPlatform();
    
    // Get device info
    const deviceInfo = {
      platform: platform,
      userAgent: navigator.userAgent,
      language: navigator.language,
      timestamp: new Date().toISOString()
    };
    
    console.log('📤 Registering device token with backend...');
    console.log('   Token:', token.substring(0, 20) + '...');
    console.log('   Platform:', platform);
    
    const response = await axios.post(
      `${API_URL}/api/push/register-token`,
      {
        token: token,
        platform: platform,
        device_info: deviceInfo
      },
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data.success) {
      console.log('✅ Device token registered with backend!');
      console.log('   Subscribed to topics:', response.data.subscribed_topics);
      
      // Store token locally
      localStorage.setItem('fcm_token', token);
    } else {
      console.log('⚠️ Backend registration response:', response.data);
    }
    
  } catch (error) {
    console.error('❌ Error registering device token:', error.response?.data || error.message);
  }
};

/**
 * Manually register token (can be called from native code bridge)
 */
export const manuallyRegisterToken = async (token) => {
  console.log('📱 Manual token registration requested:', token?.substring(0, 20) + '...');
  await registerDeviceToken(token);
};

// Expose function globally for native code to call
if (typeof window !== 'undefined') {
  window.registerPushToken = manuallyRegisterToken;
}

/**
 * Unregister device token
 */
export const unregisterDeviceToken = async () => {
  try {
    const token = localStorage.getItem('fcm_token');
    
    if (!token) {
      return;
    }
    
    const authToken = sessionStorage.getItem('access_token');
    
    if (!authToken) {
      return;
    }
    
    await axios.delete(
      `${API_URL}/api/push/unregister-token`,
      {
        data: { token: token },
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    // Remove from local storage
    localStorage.removeItem('fcm_token');
    
    console.log('✅ Device token unregistered');
    
  } catch (error) {
    console.error('❌ Error unregistering device token:', error);
  }
};

/**
 * Handle notification click
 */
const handleNotificationClick = (notification) => {
  console.log('🔔 Handling notification click:', notification);
  
  const data = notification.data;
  
  if (!data) {
    return;
  }
  
  // Notificação com imagem: abrir o flyer em tela cheia (com X para fechar)
  if (data.image) {
    const detail = {
      image: data.image,
      title: notification.title || data.flyer_title || '',
      body: notification.body || data.flyer_body || ''
    };
    // Pequeno delay para garantir que o app terminou de montar (cold start)
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('push-flyer', { detail }));
    }, 400);
    return;
  }
  
  // Route based on notification type
  switch (data.type) {
    case 'new_job':
    case 'job_updated':
    case 'job_reminder':
      // Open calendar
      window.location.hash = '#/calendar';
      break;
      
    case 'labor_bill':
      // Open labor bills
      window.location.hash = '#/labor-bills';
      break;
      
    case 'truck_rental':
      // Open truck rentals
      window.location.hash = '#/trucks';
      break;
      
    default:
      // Open home
      window.location.hash = '#/';
  }
};

/**
 * Get current platform
 */
const getPlatform = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  } else if (/android/i.test(userAgent)) {
    return 'android';
  } else {
    return 'web';
  }
};

/**
 * Get notification permission status
 */
export const getNotificationPermissionStatus = async () => {
  try {
    const status = await PushNotifications.checkPermissions();
    return status.receive; // 'granted', 'denied', or 'prompt'
  } catch (error) {
    console.error('Error checking notification permission:', error);
    return 'unknown';
  }
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  try {
    const permission = await PushNotifications.requestPermissions();
    
    if (permission.receive === 'granted') {
      await PushNotifications.register();
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Get delivered notifications (iOS only)
 */
export const getDeliveredNotifications = async () => {
  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch (error) {
    console.error('Error getting delivered notifications:', error);
    return [];
  }
};

/**
 * Remove delivered notifications (iOS only)
 */
export const removeDeliveredNotifications = async (notifications) => {
  try {
    await PushNotifications.removeDeliveredNotifications({
      notifications: notifications
    });
  } catch (error) {
    console.error('Error removing delivered notifications:', error);
  }
};

/**
 * Remove all delivered notifications (iOS only)
 */
export const removeAllDeliveredNotifications = async () => {
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.error('Error removing all delivered notifications:', error);
  }
};
